import moment from 'moment';

export const parseUTCDateFromFormat = ({ dateStr, format = 'MM/DD/YYYY' }) => {
  const utcMoment = moment.utc(dateStr, format, true); // strict UTC parsing
  return utcMoment.isValid() ? utcMoment.toDate() : null;
};

export const formatToDateTimeString = (dateStr) => {
  if (!dateStr) return 'N/A';

  const parsed = moment(dateStr, 'YYYY-MM-DD HH:mm:ss', true); // strict parsing
  return parsed.isValid()
    ? parsed.format('DD-MMM-YYYY hh:mm A')
    : 'Invalid Date';
};

/**
 * Formats a date object to a string using local time.
 *
 * @param {Object} params
 * @param {Date|string} params.date - The date to format.
 * @param {string} [params.format='MM/DD/YYYY'] - The desired format string.
 * @return {string} Formatted date string.
 */
export const formatDisplayDateHelper = ({ date, format = 'MM/DD/YYYY' }) => {
  return date ? moment(date).format(format) : '';
};

/**
 * Formats a date object to ISO format (YYYY-MM-DD).
 *
 * @param {Object} params
 * @param {Date|string} params.date - The date to format.
 * @param {string} [params.format='YYYY-MM-DD'] - The desired ISO format string.
 * @return {string} ISO formatted date string.
 */
export const formatDateHelper = ({ date, format = 'YYYY-MM-DD' }) => {
  return date ? moment(date).format(format) : '';
};

/**
 * Normalizes a date to midnight (00:00:00) in local time.
 *
 * @param {Date|string} date - The date to normalize.
 * @return {Date} Date object at the start of the day.
 */
export const normalizeToStartOfDayHelper = (date) => {
  return moment(date).startOf('day').toDate();
};

/**
 * Calculates the difference in full days between two dates (local time).
 *
 * @param {Date|string} d1 - The first date.
 * @param {Date|string} d2 - The second date.
 * @return {number} The number of full days between the two dates.
 */
export const diffInDaysHelper = (d1, d2) => {
  return moment(d1).startOf('day').diff(moment(d2).startOf('day'), 'days');
};

export const calculatePastAndFutureDaysHelper = ({
  today,
  startDate,
  endDate,
}) => {
  if (
    moment(today).isSameOrAfter(startDate, 'day') &&
    moment(today).isSameOrBefore(endDate, 'day')
  ) {
    return {
      past: diffInDaysHelper(today, startDate),
      future: diffInDaysHelper(endDate, today),
    };
  }

  return { past: null, future: null };
};

/**
 * Adds a given amount of time to a date and returns a moment object at start of day.
 *
 * @param {Date|string} date - The original date.
 * @param {number} value - The number of units to add.
 * @param {string} [unit='days'] - The unit of time to add (e.g., 'days', 'months', 'years').
 * @return {moment.Moment} Moment object with time added and normalized to start of day.
 */
export function addDaysToDateHelper(date, value, unit = 'days') {
  return moment(date).add(value, unit).startOf('day');
}

/**
 * Subtracts a given amount of time from a date and returns a moment object at start of day.
 *
 * @param {Date|string} date - The original date.
 * @param {number} value - The number of units to subtract.
 * @param {string} [unit='days'] - The unit of time to subtract (e.g., 'days', 'months', 'years').
 * @return {moment.Moment} Moment object with time subtracted and normalized to start of day.
 */
export function subtractDaysFromDateHelper(date, value, unit = 'days') {
  return moment(date).subtract(value, unit).startOf('day');
}

/**
 * Parses a release date string in YYYY-MM-DD format from backend.
 * Returns a moment object for date calculations.
 *
 * @param {string|moment.Moment|Date} dateStr - The date to parse (YYYY-MM-DD format from backend).
 * @return {moment.Moment|null} Parsed moment object or null if invalid.
 */
export function parseDateHelper(dateStr) {
  if (!dateStr) return null;

  // If already a moment object, return it
  if (moment.isMoment(dateStr)) {
    return dateStr.isValid() ? dateStr : null;
  }

  // If it's a Date object, convert to moment
  if (dateStr instanceof Date) {
    const m = moment(dateStr);
    return m.isValid() ? m : null;
  }

  // Parse string as ISO format (YYYY-MM-DD) - strict parsing
  const parsed = moment(dateStr, 'YYYY-MM-DD', true);
  if (parsed.isValid()) {
    return parsed;
  }

  // Fallback: try non-strict parsing
  const fallback = moment(dateStr);
  return fallback.isValid() ? fallback : null;
}

/**
 * Formats a release date for display using formatDisplayDateHelper.
 * Accepts YYYY-MM-DD from backend and displays as DD-MMM-YYYY.
 *
 * @param {string|Date|moment.Moment} date - The date to format.
 * @return {string} Formatted date string (DD-MMM-YYYY) or empty string if invalid.
 */
export function formatReleaseDateForDisplay(date) {
  if (!date) return '';

  const parsed = parseDateHelper(date);
  if (parsed && parsed.isValid()) {
    return formatDisplayDateHelper({
      date: parsed.toDate(),
      format: 'DD-MMM-YYYY',
    });
  }

  return '';
}

/**
 * Checks if a release date is in the past (before today).
 * Optimized to parse date once and use moment for calculation.
 *
 * @param {string|moment.Moment|Date} releaseDate - The release date to check.
 * @return {boolean} True if date is in the past, false otherwise.
 */
export function checkIsPastDate(releaseDate) {
  const parsed = parseDateHelper(releaseDate);
  if (!parsed) return false;

  return parsed.isBefore(moment(), 'day');
}

/**
 * Gets the status color for a release based on status and date.
 * Optimized to avoid redundant date parsing.
 *
 * @param {string} releaseStatus - The release status.
 * @param {string|moment.Moment|Date} releaseDate - The release date.
 * @return {string} Color code for the status.
 */
export function getReleaseStatusColor(releaseStatus, releaseDate) {
  if (releaseStatus === 'Released') {
    return '#38761d'; // Green
  }

  if (releaseStatus === 'Deleted') {
    return '#cc0000'; // Red
  }

  if (releaseStatus === 'Not Released' && checkIsPastDate(releaseDate)) {
    return '#cc0000'; // Red for past releases
  }

  return '#999999'; // Gray for future/other
}

/**
 * Format date string or Date object with multiple format options
 * @param {string | Date | null | undefined} value - The date to format
 * @param {string} format - Format style ('short', 'long', 'relative')
 * @return {string} Formatted date
 */
export const formatDate = (value, format = 'short') => {
  if (!value) {
    return '—';
  }
  const date = typeof value === 'string' ? new Date(value) : value;

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  switch (format) {
    case 'long':
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    case 'relative': {
      const diffMs = Date.now() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      return `${diffDays} days ago`;
    }
    default: // 'short'
      return date.toLocaleDateString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      });
  }
};

/**
 * Format days remaining, including null handling
 * @param {number | null | undefined} value - Number of days
 * @return {string} Formatted days or "—"
 */
export const formatDays = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  const days = Math.round(value);
  return days === 1 ? '1 day' : `${days} days`;
};
